import {
  QueryClient,
  noop,
  onlineManager,
  type InfiniteData,
  type InfiniteQueryExecuteOptions,
  type QueryExecuteOptions,
  type QueryKey,
} from '@tanstack/react-query'

import { ApiError } from '@/api/errors'
import { config } from '@/lib/config'

/**
 * Cache defaults tuned to the backend's own cadence.
 *
 * Django refreshes live prices on a 60s cron and market depth on 30s, so a
 * 30s staleTime means a refetch inside that window would return identical
 * bytes. Retrying a 4xx is worse than useless: SUBSCRIPTION_REQUIRED and
 * VALIDATION_ERROR will fail identically three times and triple the latency
 * before the user sees the paywall.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // 429 is worth backing off into; other 4xx are terminal.
          if (error.status === 429) return failureCount < 2
          if (error.isClientError) return false
        }
        return failureCount < 2
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
    },
    mutations: {
      // A write that failed on a 4xx will fail the same way again.
      retry: false,
    },
  },
})

/** Poll interval for live market data, or false when the market is shut. */
export const livePolling = (isOpen: boolean): number | false => (isOpen ? config.livePollMs : false)

/**
 * A route's main data was asked for while the device had no network and no
 * copy of it. Thrown by `ensureRouteData` so the route's error component can
 * say "offline" rather than "failed" — see RouteErrorPage.
 */
export class OfflineError extends Error {
  constructor() {
    super('The device is offline and this page has not been loaded before')
    this.name = 'OfflineError'
  }
}

/**
 * Only `false` is evidence, as `useOnline` explains: React Query's own
 * manager starts out online and only hears the `online`/`offline` events, so
 * a tab opened with no network is only caught by `navigator.onLine`.
 */
const isDeviceOffline = (): boolean =>
  !onlineManager.isOnline() || (typeof navigator !== 'undefined' && !navigator.onLine)

/**
 * The loader's door for a page's MAIN data: resolves once the data is in the
 * cache, which is what lets the page render it on its first frame.
 *
 * Cached data is returned as it is, however old — `staleTime: 'static'` is
 * query-core 5.102's spelling of the deprecated `ensureQueryData`. Freshness
 * is the screen's hook's job, not the loader's: its `useSuspenseQuery`
 * mounts on the cached copy and revalidates in the background under its own
 * `staleTime`, so a hover or a revisit never waits on a refetch of data it
 * can already draw, and never issues one either.
 *
 * With nothing cached and no network, React Query would PAUSE the fetch until
 * the network returns — and a navigation awaiting it would sit on its pending
 * skeleton indefinitely. Failing fast instead lets the route say "offline".
 */
export function ensureRouteData<TData, TKey extends QueryKey>(
  client: QueryClient,
  options: QueryExecuteOptions<TData, Error, TData, TData, TKey>,
): Promise<TData> {
  if (client.getQueryState(options.queryKey)?.data === undefined && isDeviceOffline()) {
    return Promise.reject(new OfflineError())
  }
  return client.query({ ...options, staleTime: 'static' })
}

/**
 * The loader's door for SECONDARY data: starts the read and returns at once.
 *
 * Unlike `ensureRouteData` this honours the query's own `staleTime`, so a
 * hover refreshes a card whose data has gone stale, and leaves one that is
 * fresh alone. A failure is the card's to show: it mounts on the errored
 * entry and retries it through its own hook — the same contract `usePrefetch`
 * keeps for the reads a screen starts from render.
 */
export function prefetchRouteData<TData, TKey extends QueryKey>(
  client: QueryClient,
  options: QueryExecuteOptions<TData, Error, TData, TData, TKey>,
): void {
  client.query(options).catch(noop)
}

/**
 * `ensureRouteData`'s counterpart for an infinite (load-more) main list.
 *
 * `client.query` cannot serve an infinite key: `infiniteQueryBehavior` reads
 * fields (`getNextPageParam`, the page-keyed cache shape) a plain query never
 * sets, and crashes at runtime. `ensureRouteData(infiniteQueryOptions(...))`
 * does not even get that far — `initialPageParam` is required on an infinite
 * key and merely optional on `QueryOptions`, so it fails TS2379 before the
 * page ever loads. `client.infiniteQuery` is query-core's own infinite path,
 * and `staleTime: 'static'` is its replacement for the deprecated
 * `ensureInfiniteQueryData` (see that method's own JSDoc on the installed
 * query-core) — so this mirrors `ensureRouteData` exactly, one call lower.
 *
 * The options parameter reads `InfiniteData<TData>` (page param `unknown`),
 * not `InfiniteData<TData, TPageParam>`: that is the shape an
 * `infiniteQueryOptions(...)` call actually infers for its own result — its
 * `TData` default is `InfiniteData<TQueryFnData>`, not reapplied against its
 * sibling `TPageParam` — so demanding the page-param-typed shape here would
 * reject every real caller with the same TS2379 this exists to avoid.
 * `client.infiniteQuery`'s own conditional return type still resolves to the
 * page-param-typed `InfiniteData<TData, TPageParam>` this returns, because
 * `TPageParam` itself is inferred correctly from `initialPageParam` and
 * `getNextPageParam` either way.
 */
export function ensureRouteInfiniteData<TData, TKey extends QueryKey, TPageParam>(
  client: QueryClient,
  options: InfiniteQueryExecuteOptions<TData, Error, InfiniteData<TData>, TKey, TPageParam>,
): Promise<InfiniteData<TData, TPageParam>> {
  if (client.getQueryState(options.queryKey)?.data === undefined && isDeviceOffline()) {
    return Promise.reject(new OfflineError())
  }
  return client.infiniteQuery({ ...options, staleTime: 'static' })
}

/**
 * `prefetchRouteData`'s counterpart for SECONDARY infinite data: starts the
 * read and returns at once, through the same non-deprecated `infiniteQuery`
 * door `ensureRouteInfiniteData` uses (not `prefetchInfiniteQuery`, which
 * `queryClient.infiniteQuery(options).catch(noop)` replaces). See
 * `ensureRouteInfiniteData` above for why the options parameter reads
 * `InfiniteData<TData>` rather than `InfiniteData<TData, TPageParam>`.
 */
export function prefetchRouteInfiniteData<TData, TKey extends QueryKey, TPageParam>(
  client: QueryClient,
  options: InfiniteQueryExecuteOptions<TData, Error, InfiniteData<TData>, TKey, TPageParam>,
): void {
  client.infiniteQuery(options).catch(noop)
}
